import AppToastContainer from '@/components/common/AppToastContainer'
import Loading from '@/components/common/Loading'
import { FETCHING_STATUS } from '@/config/constants'
import { IBMPlexSans } from '@/config/font'
import { selectedLocale } from '@/config/lang'
import customTheme from '@/config/theme'
import { useServiceProvider } from '@/features/onboarding/hooks/useServiceProvider'
import AuthenticatedPageLayout from '@/layouts/AuthenticatedPageLayout'
import PublicPage from '@/layouts/PublicPage'
import { AuthStatus } from '@/state/authentication'
import useStore from '@/state/store'
import '@/styles/globals.scss'
import { isPublicPrivateRoute, isPublicRoute } from '@/utils/auth'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Flowbite } from 'flowbite-react'
import type { AppPropsWithLayout } from '@/types/next-augment'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import 'react-toastify/dist/ReactToastify.min.css'
import SignifyView from '@/components/common/SignifyView'

const queryClient = new QueryClient()

const isGuestViewerRoute = (route: string) => {
  return !!route.match(/\/apps\/documents\/\[id\]\/guest-viewer/)
}

type PageAccessLevel = 'public' | 'public-private' | 'private'
const getPageAccessLevel = (route: string): PageAccessLevel => {
  if (isPublicPrivateRoute(route)) return 'public-private'
  if (isPublicRoute(route)) return 'public'
  return 'private'
}

function RenderApplication({
  isGuestViewer,
  appProps,
  pageAccessLevel
}: {
  isGuestViewer: boolean
  appProps: AppPropsWithLayout
  pageAccessLevel: PageAccessLevel
}) {
  const {
    authentication: { authStatus }
  } = useStore()

  if (authStatus === AuthStatus.Loading) {
    return <Loading />
  }

  if (isGuestViewer) {
    return <appProps.Component {...appProps.pageProps} />
  }

  if (authStatus === AuthStatus.SignedIn) {
    if (pageAccessLevel === 'private' || pageAccessLevel === 'public-private') {
      return <AuthenticatedPageLayout appProps={appProps} />
    }
    return null
  }

  if (pageAccessLevel === 'public' || pageAccessLevel === 'public-private') {
    return <PublicPage appProps={appProps} />
  }

  return null
}

export default function App(appProps: AppPropsWithLayout) {
  const router = useRouter()
  const { setSession, getMessages, authentication, localization } = useStore()

  useServiceProvider()

  const pageAccessLevel = getPageAccessLevel(router.route)
  const isGuestViewer = isGuestViewerRoute(router.route)

  useEffect(() => {
    ;(async () => {
      await getMessages(selectedLocale)
      await setSession()
    })()

    localStorage.getItem('mockIdVerification') ||
      localStorage.setItem('mockIdVerification', 'false')
    // reason: mount-once init effect; intentionally runs only on app start
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const { authStatus, userInfo } = authentication
    const isSignedIn = authStatus === AuthStatus.SignedIn
    const isSignedOut = authStatus === AuthStatus.SignedOut

    if (pageAccessLevel === 'public-private') return

    if (pageAccessLevel === 'private' && isSignedOut) {
      router.push('/auth/signin')
      return
    }

    if (pageAccessLevel === 'public' && (isSignedIn || userInfo)) {
      router.push('/')
      return
    }
  }, [router.route, authentication, pageAccessLevel, router])

  return (
    <Flowbite theme={{ theme: customTheme }}>
      <QueryClientProvider client={queryClient}>
        <IntlProvider
          locale={selectedLocale}
          messages={localization.symbolicTextIdToLocalizedText}
        >
          <main className={IBMPlexSans.className}>
            {[FETCHING_STATUS.FETCHING, FETCHING_STATUS.NOT_INITIATED].includes(
              localization.fetchingStatus
            ) ? (
              <Loading />
            ) : (
              <>
                <RenderApplication
                  isGuestViewer={isGuestViewer}
                  appProps={appProps}
                  pageAccessLevel={pageAccessLevel}
                />
                <SignifyView />
              </>
            )}
          </main>
          <AppToastContainer />
        </IntlProvider>
      </QueryClientProvider>
    </Flowbite>
  )
}
